/* ==========================================================================
   HERD — Vitality Assessment (quiz funnel)

   A single-page state machine. All copy, options and scoring weights live in
   assets/herd-quiz-data.json so editing the funnel never means touching this
   file. There is no separate opening screen — the first question is the first
   thing the reader sees, framed only by the kicker that the section renders
   server-side.

   Scoring runs "deficit-first": every option's `score` object adds points for
   the *worse* answer. At the end each axis is inverted into a vitality
   percentage and mapped into the band configured in meta.scoreBand, so the
   number shown is never a perfect 100 and never a nihilistic 0. It is a
   self-assessment score, not a diagnosis — see the fine print on the result.
   ========================================================================== */

(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-herd-quiz]';
  var STORE_KEY = 'herd-quiz-state-v1';
  var UTM_KEY = 'herd-quiz-utm-v1';

  /* ------------------------------------------------------------- utils --- */

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function clear(node) {
    while (node.firstChild) node.removeChild(node.firstChild);
  }

  // Owner-authored rich text (interstitial paragraphs, fine print). Not user
  // input, so innerHTML is acceptable here and nowhere else in this file.
  function richText(tag, className, html) {
    var node = el(tag, className);
    node.innerHTML = html;
    return node;
  }

  function fill(template, values) {
    return String(template).replace(/\{(\w+)\}/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match;
    });
  }

  function listSentence(items) {
    if (items.length === 1) return items[0];
    if (items.length === 2) return items[0] + ' and ' + items[1];
    return items.slice(0, -1).join(', ') + ' and ' + items[items.length - 1];
  }

  function track(event, params, custom) {
    if (typeof window.fbq !== 'function') return;
    try {
      window.fbq(custom ? 'trackCustom' : 'track', event, params || {});
    } catch (e) {
      /* a blocked or half-loaded pixel must never break the funnel */
    }
  }

  /* Session storage is per-tab and non-persistent: an accidental refresh keeps
     the run alive, a fresh visit always starts clean. */
  function readStore(key) {
    try {
      var raw = window.sessionStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function writeStore(key, value) {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      /* private mode / quota — the quiz still works, it just will not resume */
    }
  }

  /* Captured once on the landing hit so internal navigation cannot lose the
     campaign attribution before it reaches checkout. */
  function campaignQuery() {
    var stored = readStore(UTM_KEY);
    if (typeof stored === 'string') return stored;

    var incoming = window.location.search.replace(/^\?/, '');
    var keep = [];
    incoming.split('&').forEach(function (pair) {
      if (!pair) return;
      var name = pair.split('=')[0].toLowerCase();
      if (name.indexOf('utm_') === 0 || name === 'fbclid' || name === 'gclid' || name === 'ttclid') {
        keep.push(pair);
      }
    });

    var query = keep.join('&');
    writeStore(UTM_KEY, query);
    return query;
  }

  function withCampaign(url) {
    var query = campaignQuery();
    if (!query) return url;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + query;
  }

  /* ----------------------------------------------------------- scoring --- */

  function questionSteps(data) {
    return data.steps.filter(function (step) {
      return step.type === 'single' || step.type === 'multi';
    });
  }

  /* Highest deficit reachable per axis: a single-select can only contribute its
     single worst option, a multi-select can contribute every option at once. */
  function maxPerAxis(data) {
    var max = {};
    data.scoring.axes.forEach(function (axis) { max[axis.key] = 0; });

    questionSteps(data).forEach(function (step) {
      data.scoring.axes.forEach(function (axis) {
        var stepMax = 0;
        (step.options || []).forEach(function (option) {
          var points = (option.score || {})[axis.key] || 0;
          stepMax = step.type === 'multi' ? stepMax + points : Math.max(stepMax, points);
        });
        max[axis.key] += stepMax;
      });
    });

    return max;
  }

  function selectedOptions(data, answers) {
    var picked = [];
    questionSteps(data).forEach(function (step) {
      var answer = answers[step.id];
      if (answer == null) return;
      var values = Array.isArray(answer) ? answer : [answer];
      values.forEach(function (value) {
        var option = (step.options || []).filter(function (o) { return o.value === value; })[0];
        if (option) picked.push(option);
      });
    });
    return picked;
  }

  function computeScore(data, answers) {
    var band = (data.meta && data.meta.scoreBand) || { min: 35, max: 72 };
    var max = maxPerAxis(data);
    var deficit = {};
    data.scoring.axes.forEach(function (axis) { deficit[axis.key] = 0; });

    selectedOptions(data, answers).forEach(function (option) {
      Object.keys(option.score || {}).forEach(function (key) {
        if (deficit[key] == null) return;
        deficit[key] += option.score[key];
      });
    });

    var axes = data.scoring.axes.map(function (axis) {
      var ceiling = max[axis.key] || 0;
      var deficitPct = ceiling > 0 ? Math.min(100, (deficit[axis.key] / ceiling) * 100) : 0;
      var vitality = 100 - deficitPct;
      return {
        key: axis.key,
        label: axis.label,
        value: Math.round(band.min + (vitality / 100) * (band.max - band.min))
      };
    });

    var overall = Math.round(
      axes.reduce(function (sum, axis) { return sum + axis.value; }, 0) / (axes.length || 1)
    );

    var tier = (data.scoring.tiers || []).filter(function (t) { return overall <= t.maxScore; })[0];

    return { overall: overall, axes: axes, tier: tier ? tier.label : '', band: band };
  }

  /* The two or three answers that hurt the score most, phrased back at the
     reader in their own words. Only options carrying an `echo` qualify. */
  function scoreEchoes(data, answers) {
    return selectedOptions(data, answers)
      .filter(function (option) {
        if (!option.echo) return false;
        var score = option.score || {};
        return Object.keys(score).some(function (k) { return score[k] > 0; });
      })
      .map(function (option) {
        var score = option.score || {};
        var weight = Object.keys(score).reduce(function (sum, k) { return sum + score[k]; }, 0);
        return { echo: option.echo, weight: weight };
      })
      .sort(function (a, b) { return b.weight - a.weight; })
      .slice(0, 3)
      .map(function (entry) { return entry.echo; });
  }

  /* --------------------------------------------------------------- app --- */

  function Quiz(root) {
    this.root = root;
    this.stage = root.querySelector('[data-hq-stage]');
    this.kicker = root.querySelector('[data-hq-kicker]');
    this.progressFill = root.querySelector('[data-hq-progress]');
    this.counter = root.querySelector('[data-hq-count]');
    this.backBtn = root.querySelector('[data-hq-back]');

    this.productUrl = root.getAttribute('data-product-url') || '/';
    this.optionImages = {}; // step id -> option value -> {src, alt}
    this.screenImages = {}; // step id -> [{src, alt}]
    this.data = null;
    this.index = 0; // there is no intro screen — step 0 is the first question
    this.answers = {};
    this.started = false;
    this.completed = false;
    this.selectedPackId = null;
    this.loadingTimers = [];
  }

  /* Theme-editor image blocks arrive as one flat list; fold it into the two
     lookups the render code wants. Anything referencing a step or option that
     no longer exists simply never matches, so a stale block is harmless. */
  Quiz.prototype.loadImages = function () {
    var self = this;
    var node = this.root.querySelector('[data-hq-images]');
    if (!node) return;

    var list;
    try {
      list = JSON.parse(node.textContent);
    } catch (e) {
      return;
    }
    if (!Array.isArray(list)) return;

    list.forEach(function (entry) {
      if (!entry || !entry.src || !entry.step) return;

      if (entry.kind === 'screen_image') {
        if (!self.screenImages[entry.step]) self.screenImages[entry.step] = [];
        self.screenImages[entry.step].push({ src: entry.src, alt: entry.alt || '' });
        return;
      }

      if (!entry.option) return;
      if (!self.optionImages[entry.step]) self.optionImages[entry.step] = {};
      self.optionImages[entry.step][entry.option] = { src: entry.src, alt: entry.alt || '' };
    });
  };

  Quiz.prototype.init = function (data) {
    var self = this;
    this.data = data;
    this.loadImages();

    var saved = readStore(STORE_KEY);
    if (saved && saved.answers) {
      this.answers = saved.answers;
      this.index = typeof saved.index === 'number' ? saved.index : 0;
      this.started = !!saved.started;
      // A refresh mid-countdown should land on the result, not replay the wait.
      if (this.currentStep() && this.currentStep().type === 'loading') this.index += 1;
    }

    if (this.backBtn) {
      this.backBtn.addEventListener('click', function () { self.back(); });
    }

    this.render();
  };

  Quiz.prototype.currentStep = function () {
    return this.data.steps[this.index] || null;
  };

  Quiz.prototype.persist = function () {
    writeStore(STORE_KEY, { index: this.index, answers: this.answers, started: this.started });
  };

  // Counted on the first real answer rather than on page load, so a bounce off
  // the first question is not recorded as a started quiz.
  Quiz.prototype.markStarted = function () {
    if (this.started) return;
    this.started = true;
    track('QuizStarted', { quiz: this.data.meta.title }, true);
  };

  Quiz.prototype.next = function () {
    this.index += 1;
    this.persist();
    this.render();
  };

  Quiz.prototype.back = function () {
    if (this.index <= 0) return;
    this.index -= 1;
    this.persist();
    this.render();
  };

  /* Progress deliberately counts interstitials but not the loading screen, so
     the bar keeps moving on the teaching screens instead of stalling. */
  Quiz.prototype.updateChrome = function () {
    var trackedSteps = this.data.steps.filter(function (s) { return s.type !== 'loading'; });
    var step = this.currentStep();
    var position = step ? trackedSteps.indexOf(step) : -1;
    var isResult = this.index >= this.data.steps.length;
    // The loading screen is not a tracked step, but it comes after the last
    // question — so the bar must read full there rather than snapping to zero.
    var isLoading = step && step.type === 'loading';
    var done = isResult || isLoading ? trackedSteps.length : Math.max(0, position);
    var pct = trackedSteps.length ? (done / trackedSteps.length) * 100 : 0;

    if (this.progressFill) this.progressFill.style.width = pct.toFixed(1) + '%';

    if (this.counter) {
      this.counter.textContent = position >= 0 ? position + 1 + '/' + trackedSteps.length : '';
    }

    if (this.backBtn) {
      // Nothing behind the first question, and no going back from the score.
      this.backBtn.hidden = this.index <= 0 || isResult || isLoading;
    }

    if (this.kicker) {
      var isQuestion = step && (step.type === 'single' || step.type === 'multi');
      this.kicker.hidden = !isQuestion;
    }
  };

  Quiz.prototype.render = function () {
    this.loadingTimers.forEach(clearTimeout);
    this.loadingTimers = [];

    clear(this.stage);

    var step = this.currentStep();
    if (!step) {
      this.renderResult();
      this.updateChrome();
      return;
    }

    if (step.type === 'interstitial') this.stage.appendChild(this.buildInterstitial(step));
    else if (step.type === 'loading') this.stage.appendChild(this.buildLoading(step));
    else this.stage.appendChild(this.buildQuestion(step));

    this.updateChrome();
    this.focusStage();
  };

  Quiz.prototype.focusStage = function () {
    var heading = this.stage.querySelector('.hq-step__headline');
    if (heading) {
      heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
    }
    window.scrollTo(0, 0);
  };

  /* -------------------------------------------------------- step views --- */

  Quiz.prototype.buildQuestion = function (step) {
    var self = this;
    var wrap = el('div', 'hq-step');
    var isMulti = step.type === 'multi';

    wrap.appendChild(el('h1', 'hq-step__headline', step.headline));
    if (step.subhead) wrap.appendChild(el('p', 'hq-step__subhead', step.subhead));

    var images = this.optionImages[step.id] || {};
    // Picture cards are opt-in per question AND conditional on real images, so
    // a question marked for the grid still reads fine before any are uploaded.
    var useGrid =
      step.layout === 'grid' &&
      (step.options || []).some(function (option) { return images[option.value]; });

    var list = el('div', useGrid ? 'hq-grid' : 'hq-options');
    list.setAttribute('role', isMulti ? 'group' : 'radiogroup');
    list.setAttribute('aria-label', step.headline);

    var chosen = this.answers[step.id];
    var chosenValues = isMulti ? (chosen || []) : chosen != null ? [chosen] : [];

    (step.options || []).forEach(function (option) {
      var art = images[option.value];
      var button = el(
        'button',
        useGrid ? 'hq-gopt' : 'hq-opt' + (isMulti ? ' hq-opt--multi' : '')
      );
      button.type = 'button';
      button.setAttribute('role', isMulti ? 'checkbox' : 'radio');

      var isOn = chosenValues.indexOf(option.value) !== -1;
      button.setAttribute('aria-checked', isOn ? 'true' : 'false');
      if (isOn) button.classList.add('is-selected');

      if (useGrid) {
        var media = el('span', 'hq-gopt__img');
        if (art) {
          var img = document.createElement('img');
          img.src = art.src;
          img.alt = art.alt || '';
          img.loading = 'lazy';
          media.appendChild(img);
        }
        button.appendChild(media);
        button.appendChild(el('span', 'hq-gopt__tick'));
        // A picture card has room for a few words, not a sentence — use
        // shortLabel when the copy provides one.
        button.appendChild(el('span', 'hq-gopt__label', option.shortLabel || option.label));
      } else {
        button.appendChild(el('span', 'hq-opt__pip'));
        button.appendChild(el('span', 'hq-opt__label', option.label));
      }

      button.addEventListener('click', function () {
        if (isMulti) self.toggleMulti(step, option, list);
        else self.answerSingle(step, option);
      });

      list.appendChild(button);
    });

    wrap.appendChild(list);

    if (isMulti) {
      var cta = el('button', 'hq-cta', step.cta || 'Continue');
      cta.type = 'button';
      cta.disabled = chosenValues.length === 0;
      cta.setAttribute('data-hq-multi-cta', '');
      cta.addEventListener('click', function () { self.next(); });
      wrap.appendChild(cta);
    }

    return wrap;
  };

  // Single-select advances immediately — one tap per question, no Continue.
  Quiz.prototype.answerSingle = function (step, option) {
    this.markStarted();
    this.answers[step.id] = option.value;
    this.persist();
    this.next();
  };

  Quiz.prototype.toggleMulti = function (step, option, list) {
    this.markStarted();
    var current = this.answers[step.id] || [];
    var position = current.indexOf(option.value);

    if (position === -1) {
      // "Nothing yet" cannot coexist with an actual answer, in either direction.
      current = option.exclusive
        ? [option.value]
        : current.filter(function (value) {
            var match = step.options.filter(function (o) { return o.value === value; })[0];
            return !(match && match.exclusive);
          }).concat(option.value);
    } else {
      current = current.slice();
      current.splice(position, 1);
    }

    this.answers[step.id] = current;
    this.persist();

    Array.prototype.forEach.call(list.children, function (button, i) {
      var isOn = current.indexOf(step.options[i].value) !== -1;
      button.classList.toggle('is-selected', isOn);
      button.setAttribute('aria-checked', isOn ? 'true' : 'false');
    });

    var cta = this.stage.querySelector('[data-hq-multi-cta]');
    if (cta) cta.disabled = current.length === 0;
  };

  Quiz.prototype.buildInterstitial = function (step) {
    var self = this;
    var wrap = el('div', 'hq-step');
    var panel = el('div', 'hq-inter');

    panel.appendChild(el('h1', 'hq-step__headline', step.headline));
    (step.body || []).forEach(function (paragraph) {
      panel.appendChild(richText('p', null, paragraph));
    });

    // One image fills the width; several become a swipeable strip.
    var art = this.screenImages[step.id] || [];
    if (art.length) {
      var strip = el('div', 'hq-media' + (art.length === 1 ? ' hq-media--single' : ''));
      art.forEach(function (item) {
        var frame = el('span', 'hq-media__item');
        var img = document.createElement('img');
        img.src = item.src;
        img.alt = item.alt || '';
        img.loading = 'lazy';
        frame.appendChild(img);
        strip.appendChild(frame);
      });
      panel.appendChild(strip);
    }

    wrap.appendChild(panel);

    var cta = el('button', 'hq-cta', step.cta || 'Continue');
    cta.type = 'button';
    cta.addEventListener('click', function () { self.next(); });
    wrap.appendChild(cta);

    return wrap;
  };

  Quiz.prototype.buildLoading = function (step) {
    var self = this;
    var wrap = el('div', 'hq-step hq-loading');

    wrap.appendChild(el('div', 'hq-loading__spinner'));

    var messages = step.messages || ['Calculating your results…'];
    var msg = el('p', 'hq-loading__msg', messages[0]);
    msg.setAttribute('role', 'status');
    msg.setAttribute('aria-live', 'polite');
    wrap.appendChild(msg);

    var total = step.durationMs || 3600;
    var slice = total / messages.length;

    messages.forEach(function (text, i) {
      if (i === 0) return;
      self.loadingTimers.push(setTimeout(function () { msg.textContent = text; }, slice * i));
    });

    self.loadingTimers.push(setTimeout(function () { self.next(); }, total));

    return wrap;
  };

  /* ------------------------------------------------------------ result --- */

  Quiz.prototype.buildDial = function (score) {
    var NS = 'http://www.w3.org/2000/svg';
    var radius = 78;
    var circumference = 2 * Math.PI * radius;

    var svg = document.createElementNS(NS, 'svg');
    svg.setAttribute('class', 'hq-dial');
    svg.setAttribute('viewBox', '0 0 180 180');
    svg.setAttribute('role', 'img');
    svg.setAttribute('aria-label', 'Vitality score ' + score.overall + ' out of 100');

    function ring(className, dashOffset) {
      var circle = document.createElementNS(NS, 'circle');
      circle.setAttribute('class', className);
      circle.setAttribute('cx', '90');
      circle.setAttribute('cy', '90');
      circle.setAttribute('r', String(radius));
      circle.setAttribute('fill', 'none');
      circle.setAttribute('stroke-width', '12');
      circle.setAttribute('stroke-dasharray', String(circumference));
      if (dashOffset != null) circle.setAttribute('stroke-dashoffset', String(dashOffset));
      return circle;
    }

    svg.appendChild(ring('hq-dial__track', 0));

    var value = ring('hq-dial__value', circumference);
    value.setAttribute('transform', 'rotate(-90 90 90)');
    svg.appendChild(value);

    var num = document.createElementNS(NS, 'text');
    num.setAttribute('class', 'hq-dial__num');
    num.setAttribute('x', '90');
    num.setAttribute('y', '92');
    num.setAttribute('text-anchor', 'middle');
    num.setAttribute('dominant-baseline', 'middle');
    num.textContent = '0';
    svg.appendChild(num);

    var den = document.createElementNS(NS, 'text');
    den.setAttribute('class', 'hq-dial__den');
    den.setAttribute('x', '90');
    den.setAttribute('y', '122');
    den.setAttribute('text-anchor', 'middle');
    den.textContent = 'out of 100';
    svg.appendChild(den);

    var duration = 1100;
    var settled = false;

    // The end state must never depend on rAF: it is paused entirely while the
    // tab is backgrounded, which would otherwise leave the reader looking at a
    // score of "0". Animation is the enhancement; this is the truth.
    function settle() {
      settled = true;
      num.textContent = String(score.overall);
      value.setAttribute('stroke-dashoffset', String(circumference * (1 - score.overall / 100)));
    }

    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || document.visibilityState === 'hidden') {
      settle();
      return svg;
    }

    requestAnimationFrame(function () {
      if (settled) return;
      value.setAttribute('stroke-dashoffset', String(circumference * (1 - score.overall / 100)));

      var start = null;
      (function tick(timestamp) {
        if (settled) return;
        if (start === null) start = timestamp;
        var progress = Math.min(1, (timestamp - start) / duration);
        num.textContent = String(Math.round(score.overall * progress));
        if (progress < 1) requestAnimationFrame(tick);
        else settled = true;
      })(performance.now());
    });

    // Timers still fire in a backgrounded tab, so this closes the gap rAF leaves.
    setTimeout(function () { if (!settled) settle(); }, duration + 400);

    return svg;
  };

  Quiz.prototype.renderResult = function () {
    var self = this;
    var copy = this.data.result;
    var score = computeScore(this.data, this.answers);
    var wrap = el('div', 'hq-step hq-result');

    var head = el('div', 'hq-result__head');
    head.appendChild(el('span', 'hq-result__badge', '✓ ' + copy.badge));
    wrap.appendChild(head);

    wrap.appendChild(el('h1', 'hq-result__headline', copy.headline));
    wrap.appendChild(this.buildDial(score));
    if (score.tier) wrap.appendChild(el('p', 'hq-result__tier', score.tier));

    var echoes = scoreEchoes(this.data, this.answers);
    wrap.appendChild(
      el(
        'p',
        'hq-result__prose',
        echoes.length
          ? fill(copy.proseLead, { echoes: listSentence(echoes) })
          : copy.proseFallback
      )
    );

    // profile
    var profile = el('div', 'hq-card');
    profile.appendChild(el('h2', 'hq-card__title', copy.profileHeading));
    score.axes.forEach(function (axis) {
      var row = el('div', 'hq-axis');
      var head2 = el('div', 'hq-axis__row');
      head2.appendChild(el('span', null, axis.label));
      head2.appendChild(el('span', 'hq-axis__val', axis.value + '/100'));
      row.appendChild(head2);

      var trackEl = el('div', 'hq-axis__track');
      var fillEl = el('div', 'hq-axis__fill');
      trackEl.appendChild(fillEl);
      row.appendChild(trackEl);
      profile.appendChild(row);

      // setTimeout rather than rAF: still fires in a backgrounded tab, so the
      // bar can never be left sitting at zero width. CSS handles the easing.
      setTimeout(function () { fillEl.style.width = axis.value + '%'; }, 60);
    });
    wrap.appendChild(profile);

    // formula — the label as it actually reads, fillers included
    var formula = el('div', 'hq-card hq-formula');
    formula.appendChild(el('h2', 'hq-card__title', copy.formulaHeading));
    formula.appendChild(el('p', 'hq-formula__blend', copy.formulaBlendTitle));
    formula.appendChild(el('p', 'hq-formula__organs', copy.formulaBlendBody));
    formula.appendChild(el('p', 'hq-formula__other', copy.formulaOther));
    formula.appendChild(el('p', 'hq-formula__dose', copy.formulaDose));
    wrap.appendChild(formula);

    wrap.appendChild(el('p', 'hq-mechanism', copy.mechanism));

    // packs
    wrap.appendChild(el('h2', 'hq-card__title', copy.packHeading));
    var packs = el('div', 'hq-packs');
    packs.setAttribute('role', 'radiogroup');
    packs.setAttribute('aria-label', copy.packHeading);

    if (!this.selectedPackId) {
      var preset = copy.packs.filter(function (p) { return p.default; })[0] || copy.packs[0];
      this.selectedPackId = preset.id;
    }

    copy.packs.forEach(function (pack) {
      var button = el('button', 'hq-pack');
      button.type = 'button';
      button.setAttribute('role', 'radio');
      button.setAttribute('data-pack-id', pack.id);

      var isOn = pack.id === self.selectedPackId;
      button.setAttribute('aria-checked', isOn ? 'true' : 'false');
      if (isOn) button.classList.add('is-selected');

      if (pack.flag) button.appendChild(el('span', 'hq-pack__flag', pack.flag));
      button.appendChild(el('span', 'hq-pack__pip'));

      var body = el('span', 'hq-pack__body');
      body.appendChild(el('span', 'hq-pack__title', pack.title));
      if (pack.perDay) body.appendChild(el('span', 'hq-pack__per', pack.perDay));
      button.appendChild(body);

      var prices = el('span', 'hq-pack__prices');
      if (pack.compareAt) prices.appendChild(el('span', 'hq-pack__was', pack.compareAt));
      prices.appendChild(el('span', 'hq-pack__price', pack.price));
      button.appendChild(prices);

      button.addEventListener('click', function () {
        self.selectedPackId = pack.id;
        Array.prototype.forEach.call(packs.children, function (node) {
          var on = node.getAttribute('data-pack-id') === pack.id;
          node.classList.toggle('is-selected', on);
          node.setAttribute('aria-checked', on ? 'true' : 'false');
        });
        self.syncCta();
      });

      packs.appendChild(button);
    });
    wrap.appendChild(packs);

    var cta = el('button', 'hq-cta');
    cta.type = 'button';
    cta.setAttribute('data-hq-checkout', '');
    cta.addEventListener('click', function () { self.goToProduct(cta); });
    wrap.appendChild(cta);

    var reassure = el('ul', 'hq-reassure');
    reassure.setAttribute('data-hq-reassure', '');
    wrap.appendChild(reassure);

    if (copy.urgency) wrap.appendChild(el('p', 'hq-urgency', copy.urgency));

    var fine = el('div', 'hq-fineprint');
    fine.appendChild(el('p', null, copy.scoreNote));
    fine.appendChild(el('p', null, copy.disclaimer));
    wrap.appendChild(fine);

    this.stage.appendChild(wrap);
    this.syncCta();
    this.focusStage();

    if (!this.completed) {
      this.completed = true;
      track('QuizCompleted', { score: score.overall, tier: score.tier }, true);
      track('ViewContent', {
        content_ids: [String(this.currentPack().variantId)],
        content_type: 'product',
        content_name: this.data.meta.title,
        value: this.packValue(),
        currency: 'USD'
      });
    }
  };

  Quiz.prototype.currentPack = function () {
    var packs = this.data.result.packs;
    return packs.filter(function (p) { return p.id === this.selectedPackId; }, this)[0] || packs[0];
  };

  Quiz.prototype.packValue = function () {
    return parseFloat(String(this.currentPack().price).replace(/[^0-9.]/g, '')) || 0;
  };

  Quiz.prototype.syncCta = function () {
    var pack = this.currentPack();
    var copy = this.data.result;

    var cta = this.stage.querySelector('[data-hq-checkout]');
    if (cta) cta.textContent = fill(copy.ctaLabel, { price: pack.price, title: pack.title });

    var reassure = this.stage.querySelector('[data-hq-reassure]');
    if (reassure) {
      clear(reassure);
      (copy.reassure || []).forEach(function (line) {
        reassure.appendChild(el('li', null, fill(line, { days: pack.guaranteeDays })));
      });
    }
  };

  /* The quiz hands off to the product page — it deliberately does not add to
     cart or jump to checkout. The PDP is where the pack is chosen and the real
     AddToCart fires, which keeps this route measurable against the advertorial
     route (both are landing -> PDP -> ATC) instead of skipping a step. */
  Quiz.prototype.goToProduct = function (button) {
    var pack = this.currentPack();

    button.disabled = true;

    track('QuizCtaClick', { pack: pack.title, value: this.packValue(), currency: 'USD' }, true);

    // Lands at the top of the product page, not the offer block: the reader
    // should meet the full pitch, not be dropped straight onto the price.
    window.location.href = withCampaign(this.productUrl + '?variant=' + pack.variantId);
  };

  /* --------------------------------------------------------------- boot --- */

  function boot(root) {
    if (root.getAttribute('data-hq-bound') === 'true') return;
    root.setAttribute('data-hq-bound', 'true');

    var quiz = new Quiz(root);

    var inline = root.querySelector('[data-hq-data]');
    if (inline) {
      try {
        quiz.init(JSON.parse(inline.textContent));
        return;
      } catch (e) {
        /* fall through to the network copy */
      }
    }

    var url = root.getAttribute('data-quiz-data');
    if (!url) return;

    fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (response) { return response.json(); })
      .then(function (data) { quiz.init(data); })
      .catch(function () {
        var fallback = root.querySelector('[data-hq-fallback]');
        if (fallback) fallback.hidden = false;
      });
  }

  function init() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(boot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-bind when the section is re-rendered inside the theme editor.
  document.addEventListener('shopify:section:load', init);
})();
